import React from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/login/Login';
import Signup from './components/login/Signup';
import UserDashboard from './components/common/UserDashboard';
import ArticleListPage from './components/user/master/ArticleListPage';
import GroupCodeListPage from './components/user/master/GroupCodeListPage';
import TownVillageManagement from './components/user/master/TownVillageManagement';
import CasteManagement from './components/user/master/CasteManagement';
import TicketSettingsManagement from './components/user/master/TicketSettingsManagement';
import ReceiptSettingsManagement from './components/user/master/ReceiptSettingsManagement';
import PrinterSettingsManagement from './components/user/master/PrinterSettingsManagement';
import PrintConditionManagement from './components/user/master/PrintConditionManagement';
import FinancialYearManagement from './components/user/master/FinancialYearManagement';
import LedgerMaster from './components/user/master/LedgerMaster';
import GovernmentInterestsListPage from './components/user/master/GovernmentInterestsListPage';
import { accessLevelFromToken, homeForAccess } from './utils/sessionAccess';
import CreatePledge from './components/user/CreatePledge';
import AllPledges from './components/user/AllPledges';
import CreateReceipt from './components/user/receipt/CreateReceipt';
import AllReceipts from './components/user/AllReceipts';
import ReleasePage from './components/user/ReleasePage';
import AllReleases from './components/user/AllReleases';
import PaidExcessPledges from './components/user/PaidExcessPledges';
import PledgeFingerprintSearchPage from './components/user/PledgeFingerprintSearchPage';
import Ledgers from './pages/Ledgers';
import JournalRegister from './pages/JournalRegister';
import ProfitLossStatement from './pages/ProfitLossStatement';
import BalanceSheet from './pages/BalanceSheet';
import Daybook from './pages/Daybook';
import DebugAccounting from './pages/DebugAccounting';
import PledgeBook from './pages/PledgeBook';
import Profile from './components/user/Profile';
import ManualAccountingEntry from './components/user/ManualAccountingEntry';
import JournalVoucher from './components/user/JournalVoucher';
import AllPayments from './components/user/AllPayments';
import CashBalance from './components/user/CashBalance';
import InterestCalculation from './components/user/InterestCalculation';
import TrialBalance from './components/user/TrialBalance';
import ImportStatus from './pages/ImportStatus';
import AuctionPage from './components/user/auction/AuctionPage';
import StatutoryReports from './pages/StatutoryReports';
import BlackUserDashboard from './components/common/BlackUserDashboard';
import VatavLedgerPage from './pages/VatavLedgerPage';
import VatavTransferPage from './pages/VatavTransferPage';
import SyncStatus from './pages/SyncStatus';
import BillingSyncSettings from './components/user/master/BillingSyncSettings';
import IllegalCashBalance from './components/user/IllegalCashBalance';
import AdminDashboard from './components/admin/AdminDashboard';
import ArticleManagement from './components/admin/ArticleManagement';
import EmergencySystem from './components/admin/EmergencySystem';
import FunctionalKeysPage from './components/admin/FunctionalKeysPage';
import AdminGovernmentInterestsPage from './components/admin/GovernmentInterestsListPage';

const PrivateRoute = ({ children, area = 'main' }) => {
  const token = localStorage.getItem('token');
  if (!token) return <Navigate to="/login" replace />;

  const userType = accessLevelFromToken(token) || localStorage.getItem('userType') || 'user';
  if (area === 'admin' && userType !== 'admin') {
    return <Navigate to={homeForAccess(userType)} replace />;
  }
  if (area === 'black' && userType !== 'black') {
    return <Navigate to={homeForAccess(userType)} replace />;
  }
  if (area === 'main' && userType === 'admin') {
    return <Navigate to={homeForAccess(userType)} replace />;
  }
  return children;
};

function App() {
  return (
    <HashRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/dashboard" element={<Navigate to="/" replace />} />
        <Route path="/black-dashboard" element={<Navigate to="/black" replace />} />
        <Route path="/" element={<PrivateRoute area="main"><UserDashboard /></PrivateRoute>} />
        <Route path="/black" element={<PrivateRoute area="black"><BlackUserDashboard /></PrivateRoute>} />
        <Route path="/black/vatav-ledger" element={<PrivateRoute area="black"><VatavLedgerPage /></PrivateRoute>} />
        <Route path="/black/vatav-transfer" element={<PrivateRoute area="black"><VatavTransferPage /></PrivateRoute>} />
        <Route path="/illegal-cash-balance" element={<PrivateRoute area="black"><IllegalCashBalance /></PrivateRoute>} />
        <Route
          path="/pledge/create-secondary"
          element={<PrivateRoute area="black"><CreatePledge secondaryOnlyMode skipAccountingOnCreate={true} /></PrivateRoute>}
        />
        <Route path="/admin-dashboard" element={<PrivateRoute area="admin"><AdminDashboard /></PrivateRoute>} />
        <Route path="/admin/article-management" element={<PrivateRoute area="admin"><ArticleManagement /></PrivateRoute>} />
        <Route path="/admin/functional-keys" element={<PrivateRoute area="admin"><FunctionalKeysPage /></PrivateRoute>} />
        <Route path="/admin/government-interests" element={<PrivateRoute area="admin"><AdminGovernmentInterestsPage /></PrivateRoute>} />
        <Route path="/admin/emergency-system" element={<PrivateRoute area="admin"><EmergencySystem /></PrivateRoute>} />

        <Route path="/master/articles" element={<PrivateRoute><ArticleListPage /></PrivateRoute>} />
        <Route path="/master/group-codes" element={<PrivateRoute><GroupCodeListPage /></PrivateRoute>} />
        <Route path="/master/town-village" element={<PrivateRoute><TownVillageManagement /></PrivateRoute>} />
        <Route path="/master/caste" element={<PrivateRoute><CasteManagement /></PrivateRoute>} />
        <Route path="/master/ticket-settings" element={<PrivateRoute><TicketSettingsManagement /></PrivateRoute>} />
        <Route path="/master/receipt-settings" element={<PrivateRoute><ReceiptSettingsManagement /></PrivateRoute>} />
        <Route path="/master/printer-settings" element={<PrivateRoute><PrinterSettingsManagement /></PrivateRoute>} />
        <Route path="/master/print-conditions" element={<PrivateRoute><PrintConditionManagement /></PrivateRoute>} />
        <Route path="/master/financial-years" element={<PrivateRoute><FinancialYearManagement /></PrivateRoute>} />
        <Route path="/master/ledgers" element={<PrivateRoute><LedgerMaster /></PrivateRoute>} />
        <Route path="/master/government-interests" element={<PrivateRoute><GovernmentInterestsListPage /></PrivateRoute>} />
        <Route path="/import-status" element={<PrivateRoute><ImportStatus /></PrivateRoute>} />
        <Route path="/sync-status" element={<PrivateRoute><SyncStatus /></PrivateRoute>} />
        <Route path="/master/billing-sync" element={<PrivateRoute><BillingSyncSettings /></PrivateRoute>} />
        <Route path="/ledgers" element={<PrivateRoute><Ledgers /></PrivateRoute>} />
        <Route path="/journal-register" element={<PrivateRoute><JournalRegister /></PrivateRoute>} />
        <Route path="/profit-loss" element={<PrivateRoute><ProfitLossStatement /></PrivateRoute>} />
        <Route path="/balance-sheet" element={<PrivateRoute><BalanceSheet /></PrivateRoute>} />
        <Route path="/trial-balance" element={<PrivateRoute><TrialBalance /></PrivateRoute>} />
        <Route path="/daybook" element={<PrivateRoute><Daybook /></PrivateRoute>} />
        <Route path="/debug-accounting" element={<PrivateRoute><DebugAccounting /></PrivateRoute>} />
        <Route path="/pledge-book" element={<PrivateRoute><PledgeBook /></PrivateRoute>} />
        <Route path="/reports" element={<PrivateRoute><StatutoryReports /></PrivateRoute>} />

        <Route path="/pledge/create" element={<PrivateRoute><CreatePledge postAccountingOnEdit={true} /></PrivateRoute>} />
        <Route path="/pledge/create-no-accounting" element={<PrivateRoute><CreatePledge skipAccountingOnCreate={true} /></PrivateRoute>} />
        <Route path="/pledge/list" element={<PrivateRoute><AllPledges /></PrivateRoute>} />
        <Route path="/pledge/paid-excess" element={<PrivateRoute><PaidExcessPledges /></PrivateRoute>} />
        <Route path="/pledge/create-receipt" element={<PrivateRoute><CreateReceipt /></PrivateRoute>} />
        <Route path="/receipts/all" element={<PrivateRoute><AllReceipts /></PrivateRoute>} />
        <Route path="/release" element={<PrivateRoute><ReleasePage /></PrivateRoute>} />
        <Route path="/pledge/fingerprint-search" element={<PrivateRoute><PledgeFingerprintSearchPage /></PrivateRoute>} />
        <Route path="/releases/all" element={<PrivateRoute><AllReleases /></PrivateRoute>} />
        <Route path="/auction" element={<PrivateRoute><AuctionPage /></PrivateRoute>} />
        <Route path="/auction/catalogue" element={<PrivateRoute><AuctionPage /></PrivateRoute>} />
        <Route path="/auction/entry" element={<PrivateRoute><AuctionPage /></PrivateRoute>} />
        <Route path="/auction/list" element={<PrivateRoute><AuctionPage /></PrivateRoute>} />
        <Route path="/auction/reserve" element={<PrivateRoute><AuctionPage /></PrivateRoute>} />
        <Route path="/auction/sale-intimation" element={<PrivateRoute><AuctionPage /></PrivateRoute>} />
        <Route path="/auction/reminder" element={<PrivateRoute><AuctionPage /></PrivateRoute>} />

        <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
        <Route path="/manual-entry" element={<PrivateRoute><ManualAccountingEntry /></PrivateRoute>} />
        <Route path="/journal-voucher" element={<PrivateRoute><JournalVoucher /></PrivateRoute>} />
        <Route path="/payments/all" element={<PrivateRoute><AllPayments /></PrivateRoute>} />
        <Route path="/cash-balance" element={<PrivateRoute><CashBalance /></PrivateRoute>} />
        <Route path="/interest-calculation" element={<PrivateRoute><InterestCalculation /></PrivateRoute>} />
      </Routes>
    </HashRouter>
  );
}

export default App;
